import { stripIndents } from 'common-tags';
import { format, utcToZonedTime } from 'date-fns-tz';
import { v4 } from 'uuid';
import { formatDescription } from '@tools/formatDescription';
import { getApptICSString } from '../appointments/downloadApptCalendar';
import type { SessionType } from '../agendaCard/SessionCardType';

const getICSString = ({
  start,
  end,
  name,
  description,
  eventName,
  location,
  url
}: Pick<SessionType, 'start' | 'end' | 'name' | 'description'> & {
  eventName: string;
  location?: SessionType['location']['name'];
  url?: string;
}): string => {
  const sessionUrl = url || window.location.href;
  const icsFormat = "yyyyMMdd'T'HHmmss'Z'";

  const descriptionBody = stripIndents`
  ${eventName}\\n\\n ${sessionUrl} \\n\\n${formatDescription(description, true)}
  `;
  const descriptionBodyHTML = stripIndents`
  ${eventName}<br /><br /> ${sessionUrl} <br /><br />${formatDescription(description, false)}
  `;

  // Trick date-fns into giving us a utc date https://stackoverflow.com/questions/58561169/date-fns-how-do-i-format-to-utc/63227335#63227335
  const formatInUTC = (date: Date, fmt: string): string => {
    return format(utcToZonedTime(date, 'UTC'), fmt, { timeZone: 'UTC' });
  };
  // UID is a random identifier for calendars to separate events
  return `
    BEGIN:VEVENT
    UID:${v4()}
    DTSTAMP:${formatInUTC(new Date(start), icsFormat)}
    DTSTART:${formatInUTC(new Date(start), icsFormat)}
    DTEND:${formatInUTC(new Date(end), icsFormat)}
    SUMMARY:${name}
    X-ALT-DESC;FMTTYPE=text/html:${descriptionBodyHTML}
    ${!location ? '' : `LOCATION:${location}`}
    PRIORITY:5
    BEGIN:VALARM
    DESCRIPTION:${descriptionBody}
    TRIGGER:-PT15M
    ACTION:DISPLAY
    END:VALARM
    END:VEVENT
  `;
};

export const downloadCalendar = ({
  start,
  end,
  name,
  description,
  location,
  eventName
}: Pick<SessionType, 'start' | 'end' | 'name' | 'description'> & {
  location?: SessionType['location']['name'];
  eventName: string;
}): void => {
  const a = document.createElement('a');
  a.style.display = 'none';
  document.body.appendChild(a);

  const blobObject = new Blob(
    [
      stripIndents`
        BEGIN:VCALENDAR
        VERSION:2.0
        PRODID:-//Cvent//EN
        ${getICSString({
          start,
          end,
          name,
          description,
          eventName,
          location
        })}
        END:VCALENDAR`
    ],
    { type: 'text/calendar;charset=utf-8' }
  );

  a.href = window.URL.createObjectURL(blobObject);

  a.setAttribute('download', `${name}.ics`);
  a.click();

  setTimeout((): void => {
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
  }, 2000);
};

export const downloadSchedule = (
  sessions: SessionType[],
  eventName: string,
  translate: (key: string) => string
): void => {
  const a = document.createElement('a');
  a.style.display = 'none';
  document.body.appendChild(a);

  const icsSessions: string[] = sessions.map(sessionOrAppt => {
    const { id, start, end, name, description, locationDisplayName, location, type } = sessionOrAppt;
    if (type === 'APPOINTMENT' || type === 'PERSONAL_TIME') {
      return getApptICSString(
        {
          ...sessionOrAppt,
          startTime: start,
          endTime: end,
          loc: locationDisplayName
        },
        translate
      );
    }
    return getICSString({
      start,
      end,
      name,
      description,
      eventName,
      location: location?.name,
      url: id ? window.location.href.replace('schedule', `sessions/${id}`) : null
    });
  });

  const blobObject = new Blob(
    [
      stripIndents`
        BEGIN:VCALENDAR
        VERSION:2.0
        PRODID:-//Cvent//EN
        ${icsSessions.join(' ')}
        END:VCALENDAR`
    ],
    { type: 'text/calendar;charset=utf-8' }
  );

  a.href = window.URL.createObjectURL(blobObject);

  a.setAttribute('download', `${eventName}.ics`);
  a.click();

  setTimeout((): void => {
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
  }, 2000);
};

// Google Calendar automatically converts from UTC to the users configured time zone
export const getGoogleCalendar = ({
  name,
  start,
  end,
  eventName
}: Pick<SessionType, 'start' | 'end' | 'name'> & { eventName: string }): string => {
  const sessionUrl = window.location.href;
  const formattedStart = start.replace(/[:.-]/g, '');
  const formattedEnd = end.replace(/[:.-]/g, '');
  const url = new URLSearchParams();

  url.append('text', name);
  url.append('details', `${eventName}<br><br><a href="${sessionUrl}">${sessionUrl}</a>`);
  url.append('dates', formattedStart);

  return `https://calendar.google.com/calendar/u/0/r/eventedit?${url}/${formattedEnd}`;
};

// Outlook Calendar automatically converts from UTC to the users configured time zone
export const getOutlookCalendar = ({
  name,
  start,
  end,
  eventName
}: Pick<SessionType, 'start' | 'end' | 'name'> & { eventName: string }): string => {
  const sessionUrl = window.location.href;
  const url = new URLSearchParams();
  // the regular ampersand gets turned into "&amp;"" when sent to outlook
  // this swap fixes that issue
  const fixedName = name.replace(/&/g, '＆');

  url.append('subject', fixedName);
  url.append('body', `${eventName}<br><br><a href="${sessionUrl}">${sessionUrl}</a>`);
  url.append('startdt', start);
  url.append('enddt', end);
  url.append('path', 'calendar/action/compose');
  url.append('rru', 'addevent');

  return `https://outlook.office365.com/calendar/action/compose?${url}`;
};

// Yahoo Calendar does NOT automatically convert from UTC to the users time zone so that is done programatically with format()
export const getYahooCalendar = ({
  name,
  start,
  end,
  eventName
}: Pick<SessionType, 'start' | 'end' | 'name'> & { eventName: string }): string => {
  const sessionUrl = window.location.href;
  const formattedStart = format(new Date(start), "yyyyMMdd'T'HHmmss");
  const formattedEnd = format(new Date(end), "yyyyMMdd'T'HHmmss");
  const url = new URLSearchParams();

  url.append('title', name);
  url.append('desc', `${eventName}\n\n${sessionUrl}`);
  url.append('st', formattedStart);
  url.append('et', formattedEnd);
  url.append('v', '60'); // Can't find documentation for what this value represents but it's required

  return `https://calendar.yahoo.com/?${url}`;
};
